import React, { useEffect, useState } from "react";

// components
import { Modal, notification, Spin } from "antd";
import { InventoryReceivingItemsTable, InventoryReceivingItemsTableTypes } from "@secondcloset/web-components";

// styles
import * as S from "./styles";

// api
import { useUpdateItemStatus } from "../../../../../../hooks/api/fulfillment/shipment";

// Helpers
import { isEmpty } from "lodash-es";
import * as shipmentCardLib from "../../ShipmentCard/helper";

// types
import { Fulfillment } from "@secondcloset/types";
type ShipmentType = Fulfillment.Shipment;
type ShipmentItem = Fulfillment.ShipmentItem;
type TableItem = InventoryReceivingItemsTableTypes.Item;
type ItemStatus = InventoryReceivingItemsTableTypes.ItemStatus;

// enums
const ItemStatus = InventoryReceivingItemsTableTypes.ItemStatus;

interface Props {
  shipment: ShipmentType;
  shipmentItems?: Fulfillment.ShipmentItem<"V2">[];
  visible: boolean;
  isLoading: boolean;
  setVisible: (VisibilityState: boolean) => unknown;
  onRefetch: () => unknown;
}

const ProcessInventoryReceivingItemsModal: React.FC<Props> = ({
  shipment,
  shipmentItems,
  visible,
  isLoading,
  setVisible,
  onRefetch,
}) => {
  const [search, setSearch] = useState("");
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<ItemStatus[]>([]);
  const [selectedItemIDs, setSelectedItemIDs] = useState<string[]>([]);
  const { updateItemStatusData, updateItemStatus } = useUpdateItemStatus();

  const isShipmentCompleted = shipment?.shipping_method?.status === "completed";

  useEffect(() => {
    if (updateItemStatusData.error) {
      notification.error({
        message: "Update not successful",
        description: updateItemStatusData.error,
      });
    }
  }, [updateItemStatusData.error]);

  const getShipmentItemsV2 = () => {
    return (shipmentItems as Fulfillment.ShipmentItem<"V2">[])
      .map((item) => {
        return item.shipment_item_ids.map((itemID) => {
          return {
            id: itemID,
            sku: item.product.sku,
            name: item.product.name,
            itemStatus: shipmentCardLib.getItemStatusV2(item, itemID),
          } as TableItem;
        });
      })
      .flatMap((item) => item);
  };

  const getShipmentItems = () => {
    if (isEmpty(shipment?.shipment_items)) return [];
    return shipment.shipment_items.map((item: ShipmentItem) => {
      return {
        id: item.id,
        sku: item.item_identifier_value,
        name: item.description,
        itemStatus: shipmentCardLib.getItemStatus(item),
      } as TableItem;
    });
  };

  const resetFields = () => {
    setSearch("");
    setSelectedStatusFilter([]);
    setSelectedItemIDs([]);
  };

  const onUpdateItemStatus = (itemIDs: string[], newStatus: ItemStatus) => {
    const body = shipmentCardLib.constructUpdateItemStatusBody(itemIDs, newStatus);

    updateItemStatus(shipment.id, body).then((success: boolean) => {
      if (success) {
        resetFields();
        onRefetch();
      }
    });
  };

  const renderTable = () => {
    return !!isLoading ? (
      <Spin spinning={true}></Spin>
    ) : (
      <S.TableContainer>
        <InventoryReceivingItemsTable
          viewOnly={isShipmentCompleted}
          search={search}
          selectedStatusFilter={selectedStatusFilter}
          selectedItemIDs={selectedItemIDs}
          shipmentItems={shipmentItems ? getShipmentItemsV2() : getShipmentItems()}
          onItemStatusUpdate={onUpdateItemStatus}
          tableDataLoading={updateItemStatusData.loading || isLoading}
          setSearch={setSearch}
          setSelectedStatusFilter={setSelectedStatusFilter}
          setSelectedItemIDs={setSelectedItemIDs}
        ></InventoryReceivingItemsTable>
      </S.TableContainer>
    );
  };
  return (
    <Modal visible={visible} onCancel={() => setVisible(false)} footer={null} width={1000}>
      {renderTable()}
    </Modal>
  );
};

export default ProcessInventoryReceivingItemsModal;
